import type { ApolloError } from "@apollo/client"
import { addMinutes } from "date-fns"
import cn from "@/v2-ui/utils/utils.cn"
import Separator from "@/v2-ui/separator"
import { DotText } from "@/v2-backoffice-client/components/dot"
import Table, {
  createColumns,
  type TableProps,
} from "@/v2-backoffice-client/components/table"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import type { InvitationFragment } from "@/v2-backoffice-client/invitation/__types__/invitation.fragment"
import InvitationResendManyButton from "@/v2-backoffice-client/invitation/resend/many/InvitationResendManyButton"
import InvitationRemoveManyButton from "@/v2-backoffice-client/invitation/remove/many/InvitationRemoveManyButton"
import InvitationLogPopover from "@/v2-backoffice-client/invitation/log/InvitationLogPopover"
import TenantInvitationTableRowToolbar from "@/v2-backoffice-client/tenant/invitation/TenantInvitationTableRowToolbar"
import TenantInvitationCreateButton from "@/v2-backoffice-client/tenant/invitation/create/TenantInvitationCreateButton"

function formatRelativeExpiry(expiresAt: Date) {
  const now = new Date()
  const diffMs = expiresAt.getTime() - now.getTime()

  if (diffMs <= 0) {
    const agoMs = -diffMs
    const agoMinutes = Math.round(agoMs / 60_000)
    if (agoMinutes < 60) return `Expired ${agoMinutes}m ago`
    const agoHours = Math.round(agoMs / 3_600_000)
    if (agoHours < 24) return `Expired ${agoHours}h ago`
    const agoDays = Math.round(agoMs / 86_400_000)
    return `Expired ${agoDays}d ago`
  }

  const totalMinutes = Math.round(diffMs / 60_000)
  if (totalMinutes <= 60) return `${totalMinutes}m left`

  const totalHours = diffMs / 3_600_000
  if (totalHours <= 24) {
    const roundedHours = Math.round(totalHours)
    return `${roundedHours}h left`
  }

  const totalDays = diffMs / 86_400_000
  const roundedDays = Math.round(totalDays)
  if (roundedDays <= 1) return `${Math.round(totalHours)}h left`
  return `${roundedDays}d left`
}

const columns = createColumns<InvitationFragment>({
  enableSelection: true,
  enableSorting: true,
  enableHiding: true,
  columns: {
    Email: {
      id: "Email",
      label: "Email",
    },
    Name: {
      id: "Name",
      label: "Name",
      getValue: (row) => formatUserName(row.FirstName, row.LastName),
      renderCell({ row }) {
        return formatUserName(row.original.FirstName, row.original.LastName)
      },
    },
    UserTenantType: {
      id: "UserTenantType",
      label: "Role",
      enableGrouping: true,
      getValue: (row) => row.UserTenantType?.Name,
      filter: {
        type: "select",
        placeholder: "Filter by role...",
        options: [
          { label: "Admin", value: "1" },
          { label: "User", value: "2" },
          { label: "Viewer", value: "3" },
        ],
      },
    },
    Referral: {
      id: "Referral",
      label: "Referral",
    },
    LatestLog: {
      id: "LatestLog",
      label: "Status",
      getValue: (row) => row.LatestLog?.StatusName,
      renderCell({ row }) {
        return (
          <InvitationLogPopover
            latestLog={row.original.LatestLog}
            logs={row.original.Logs}
          />
        )
      },
    },
    ExpiresIn: {
      id: "ExpiresIn",
      label: "Expires In",
      getValue: (row) => {
        return addMinutes(new Date(row.CreatedAt), row.MinuteLimit).getTime()
      },
      renderCell({ row }) {
        const inv = row.original
        const createdAt = new Date(inv.CreatedAt)
        const expiresAt = addMinutes(createdAt, inv.MinuteLimit)
        const isExpired = expiresAt.getTime() <= Date.now()
        return (
          <DotText variant={isExpired ? "destructive" : "success-1"}>
            {formatRelativeExpiry(expiresAt)}
          </DotText>
        )
      },
      exportValue({ row }) {
        const log = row.original.LatestLog
        if (!log) return null
        const expiresAt = addMinutes(
          new Date(log.CreatedAt),
          row.original.MinuteLimit,
        )
        return formatRelativeExpiry(expiresAt)
      },
    },
    Id: { id: "Id", label: "Id", defaultSize: 50, defaultHidden: true },
  },
})

export type TenantInvitationTableProps = Omit<
  TableProps<InvitationFragment>,
  "data" | "columns"
> & {
  tenantId?: number
  invitations: InvitationFragment[]
  isLoading?: boolean
  error?: ApolloError | Error
  onRefresh?(): void
}

function TenantInvitationTable(props: TenantInvitationTableProps) {
  const {
    className,
    tenantId,
    invitations,
    onRefresh,
    isLoading,
    error,
    ...tableProps
  } = props

  return (
    <Table
      className={cn("tenant-invitation-table", className)}
      tableId="tenant-invitation-table"
      data={invitations}
      columns={columns}
      enableRowSelection
      {...tableProps}
    >
      <Table.Toolbar>
        <div className="flex-1" />
        <Table.Search column="Email" placeholder="Search by email..." />
        <Separator orientation="vertical" />

        <Table.ToolbarMenu onRefresh={onRefresh} exportFileName="invitations">
          {tenantId && <TenantInvitationCreateButton tenantId={tenantId} />}
        </Table.ToolbarMenu>
      </Table.Toolbar>

      <Table.FilterList />

      <Table.RowToolbar<InvitationFragment> column="Email">
        {(invitation) => (
          <TenantInvitationTableRowToolbar invitation={invitation} />
        )}
      </Table.RowToolbar>

      <Table.Content
        countLabel="invitations"
        isLoading={isLoading}
        error={error}
      />

      <Table.SelectionToolbar<InvitationFragment>>
        {(invitations, reset) => {
          const ids = invitations.map((inv) => inv.Id)
          return (
            <>
              <Table.ExportSelectionButton
                fileName="invitations"
                selectionCount={invitations.length}
              />
              <InvitationResendManyButton
                isStacked
                invitationIds={ids}
                onCompleted={reset}
              >
                Resend
              </InvitationResendManyButton>
              <InvitationRemoveManyButton
                isStacked
                invitationIds={ids}
                onCompleted={reset}
              >
                Remove
              </InvitationRemoveManyButton>
            </>
          )
        }}
      </Table.SelectionToolbar>
    </Table>
  )
}

export default TenantInvitationTable
